<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import Icon from './Icon.svelte';
  import type { ApiField, ApiRecord } from '../lib/data-tables-api';
  import type { KanbanCardConfig, CardSize } from '../lib/kanban-config';
  import type { KanbanSource } from '../lib/kanban-api';
  import { columnAccent, type ColumnAccent } from '../lib/kanban-colors';
  import { loadColumnWidths, saveColumnWidths } from '../lib/kanban-config';
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import { flip } from 'svelte/animate';
  import FieldDisplay from '../lib/fields/FieldDisplay.svelte';
  import FieldEditor from '../lib/fields/FieldEditor.svelte';
  import Avatar from './Avatar.svelte';
  import { formatCurrencyCents, formatNumber, formatPercent } from '../lib/fields/format';
  import countries from '../lib/countries.json';

  let {
    slug,
    groupFieldKey,
    groupFieldType = 'dropdown',
    source,
    reloadToken = 0,
    canDragGroup = false,
    cardPageSize = 30,
    fields,
    config,
    onConfigClick,
    onRecordClick,
    onCellEdit,
    onCardMove,
    canEdit,
    showAvatar = false,
    selectable = false,
    selectedIds = new Set<string>(),
    onSelectionChange,
  }: {
    slug: string;
    groupFieldKey: string;
    groupFieldType?: string;
    source: KanbanSource;
    /** Змінюється коли змінились фільтри/поле/granularity → дошка перевантажується. */
    reloadToken?: number;
    /** Чи можна переміщувати картки (group-поле settable + editable). */
    canDragGroup?: boolean;
    cardPageSize?: number;
    fields: ApiField[];
    config: KanbanCardConfig;
    onConfigClick: () => void;
    onRecordClick: (id: string) => void;
    onCellEdit?: (recordId: string, field: ApiField, value: unknown) => Promise<void>;
    /** Переміщення картки → встановити group-поле у значення колонки. */
    onCardMove?: (recordId: string, newValue: unknown) => Promise<void>;
    canEdit?: (field: ApiField) => boolean;
    showAvatar?: boolean;
    selectable?: boolean;
    selectedIds?: Set<string>;
    onSelectionChange?: (ids: Set<string>) => void;
  } = $props();

  interface BoardCol {
    key: string;
    label: string;
    color: ColumnAccent;
    count: number;
    records: ApiRecord[];
    page: number;
    loadingMore: boolean;
  }

  let cols = $state<BoardCol[]>([]);
  let loading = $state(true);
  let facetsColPage = $state(1);
  let facetsHasMore = $state(false);
  let loadingColumns = $state(false);
  let err = $state<string | null>(null);

  /** Мапа card.id → ключ колонки (для детекції drag-переміщень). */
  let cardCol = new Map<string, string>();
  function reindexCardCol() {
    cardCol = new Map();
    for (const c of cols) for (const r of c.records) cardCol.set(r.id, c.key);
  }

  function accentFor(key: string): ColumnAccent {
    return columnAccent(key === '__none__' ? null : key);
  }

  const countryByCode: Record<string, { name: string; flag: string }> = Object.fromEntries(
    (countries as { code: string; name: string; flag: string }[]).map((c) => [c.code, { name: c.name, flag: c.flag }]),
  );

  /** Підпис колонки за типом поля групування (сирий ключ → людський формат). */
  function displayLabel(col: { key: string; label: string }): string {
    if (col.key === '__none__') return col.label;
    switch (groupFieldType) {
      case 'currency': return formatCurrencyCents(Number(col.key));
      case 'number':
      case 'auto_number': return formatNumber(Number(col.key));
      case 'percent': return formatPercent(Number(col.key));
      case 'country': {
        const c = countryByCode[col.key.toUpperCase()];
        return c ? `${c.flag} ${c.name}` : col.label;
      }
      default: return col.label;
    }
  }

  /** Завантажити першу сторінку карток колонки і повернути ГОТОВИЙ об'єкт колонки. */
  async function buildColumn(c: { key: string; label: string; count: number }): Promise<BoardCol> {
    let records: ApiRecord[] = [];
    let total = c.count;
    try {
      const r = await source.loadCards(c.key, 1);
      records = r.records;
      total = r.total;
    } catch {
      // лишаємо порожньою — кнопка "Ще" дасть повторити
    }
    return { key: c.key, label: c.label, color: accentFor(c.key), count: total, records, page: 1, loadingMore: false };
  }

  async function reload() {
    loading = true;
    err = null;
    facetsColPage = 1;
    // Зміна фільтра/поля/granularity → вибір втрачає сенс, скидаємо.
    if (selectedIds.size > 0) onSelectionChange?.(new Set());
    try {
      const f = await source.loadFacets(1);
      facetsHasMore = f.hasMore;
      cols = await Promise.all(f.columns.map(buildColumn));
      reindexCardCol();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  // Перевантаження при зміні фільтрів/поля/granularity.
  let lastToken = -1;
  $effect(() => {
    if (reloadToken !== lastToken) {
      lastToken = reloadToken;
      void reload();
    }
  });

  async function loadMoreColumns() {
    if (loadingColumns || !facetsHasMore) return;
    loadingColumns = true;
    try {
      const next = facetsColPage + 1;
      const f = await source.loadFacets(next);
      facetsColPage = next;
      facetsHasMore = f.hasMore;
      const newCols = await Promise.all(f.columns.map(buildColumn));
      cols = [...cols, ...newCols];
      reindexCardCol();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loadingColumns = false;
    }
  }

  async function loadMoreCards(col: BoardCol) {
    const idx = cols.findIndex((c) => c.key === col.key);
    if (idx < 0 || cols[idx].loadingMore || cols[idx].records.length >= cols[idx].count) return;
    cols[idx] = { ...cols[idx], loadingMore: true };
    cols = [...cols];
    try {
      const r = await source.loadCards(col.key, cols[idx].page + 1);
      cols[idx] = {
        ...cols[idx],
        records: [...cols[idx].records, ...r.records],
        page: cols[idx].page + 1,
        count: r.total,
        loadingMore: false,
      };
      cols = [...cols];
      reindexCardCol();
    } catch (e) {
      cols[idx] = { ...cols[idx], loadingMore: false };
      cols = [...cols];
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  /** Горизонтальний скрол → довантаження колонок під кінець. */
  function onBoardScroll(e: Event) {
    const el = e.currentTarget as HTMLElement;
    if (el.scrollLeft + el.clientWidth > el.scrollWidth - 600) void loadMoreColumns();
  }

  /* ── Drag move між колонками ──────────────────────────────────────────────── */
  function colMoveValue(key: string): unknown {
    if (key === '__none__') return null;
    if (groupFieldType === 'checkbox') return key === 'true';
    if (groupFieldType === 'number' || groupFieldType === 'currency' || groupFieldType === 'percent' || groupFieldType === 'auto_number') {
      const n = Number(key);
      return Number.isFinite(n) ? n : key;
    }
    return key;
  }

  function onConsider(i: number, e: CustomEvent<DndEvent<ApiRecord>>) {
    cols[i].records = e.detail.items;
  }
  async function onFinalize(i: number, e: CustomEvent<DndEvent<ApiRecord>>) {
    cols[i].records = e.detail.items;
    if (!onCardMove) return;
    const target = cols[i];
    const incoming = e.detail.items.filter((r) => cardCol.get(r.id) !== target.key);
    for (const rec of incoming) {
      const fromKey = cardCol.get(rec.id);
      cardCol.set(rec.id, target.key);
      target.count += 1;
      const src = cols.find((c) => c.key === fromKey);
      if (src) src.count = Math.max(0, src.count - 1);
      try {
        await onCardMove(rec.id, colMoveValue(target.key));
      } catch (er) {
        showAlert(er instanceof Error ? er.message : String(er));
        await reload();
        return;
      }
    }
  }

  /* ── Resize колонок ───────────────────────────────────────────────────────── */
  const DEFAULT_W = 272, MIN_W = 200, MAX_W = 460;
  let widths = $state<Record<string, number>>({});
  $effect(() => { widths = loadColumnWidths(slug, groupFieldKey ?? ''); });
  function colWidth(key: string): number { return widths[key] ?? DEFAULT_W; }
  let resizing: { key: string; startX: number; startW: number } | null = null;
  function startResize(e: PointerEvent, key: string) {
    e.preventDefault(); e.stopPropagation();
    resizing = { key, startX: e.clientX, startW: colWidth(key) };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onResizeMove(e: PointerEvent) {
    if (!resizing) return;
    const w = Math.min(MAX_W, Math.max(MIN_W, resizing.startW + (e.clientX - resizing.startX)));
    widths = { ...widths, [resizing.key]: w };
  }
  function endResize() {
    if (!resizing) return;
    saveColumnWidths(slug, groupFieldKey ?? '', widths);
    resizing = null;
  }

  /* ── Collapse ─────────────────────────────────────────────────────────────── */
  let collapsed = $state<Record<string, boolean>>({});
  function toggleCollapse(key: string) { collapsed = { ...collapsed, [key]: !collapsed[key] }; }

  /* ── Мультивибір ──────────────────────────────────────────────────────────── */
  const flatIds = $derived(cols.flatMap((c) => c.records.map((r) => r.id)));
  let lastClicked: string | null = null;
  function emit(next: Set<string>) { onSelectionChange?.(next); }
  function toggleOne(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    lastClicked = id; emit(next);
  }
  function selectRange(id: string) {
    if (!lastClicked) { toggleOne(id); return; }
    const a = flatIds.indexOf(lastClicked), b = flatIds.indexOf(id);
    if (a < 0 || b < 0) { toggleOne(id); return; }
    const [lo, hi] = a < b ? [a, b] : [b, a];
    const next = new Set(selectedIds);
    for (let k = lo; k <= hi; k++) next.add(flatIds[k]!);
    emit(next);
  }
  function onCheckboxClick(e: MouseEvent, id: string) {
    e.stopPropagation();
    if (e.shiftKey) selectRange(id); else toggleOne(id);
  }
  /** Вибрати/зняти ВСІ картки колонки (через сервер — не лише завантажені).
   *  Повторний клік, коли всі вже вибрані → знімає вибір цієї колонки. */
  let selectingCol = $state<string | null>(null);
  async function selectAllInColumn(col: BoardCol) {
    selectingCol = col.key;
    try {
      const ids = await source.loadIds(col.key);
      const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
      const next = new Set(selectedIds);
      if (allSelected) for (const id of ids) next.delete(id);
      else for (const id of ids) next.add(id);
      emit(next);
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    } finally {
      selectingCol = null;
    }
  }

  /* ── Інлайн-редагування (попап) ───────────────────────────────────────────── */
  let editing = $state<{ recordId: string; field: ApiField } | null>(null);
  let editDraft = $state<unknown>(undefined);
  let editBusy = $state(false);
  let editAnchor = $state<{ x: number; y: number } | null>(null);

  function isFieldEditable(f: ApiField): boolean {
    if (!onCellEdit || f.is_computed || f.type === 'linked_summary' || f.type === 'formula' || f.type === 'stat') return false;
    return canEdit ? canEdit(f) : true;
  }
  function startEdit(e: Event, recordId: string, field: ApiField, value: unknown) {
    if (!isFieldEditable(field)) return;
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const W = 300;
    editAnchor = {
      x: Math.min(Math.max(8, rect.left), window.innerWidth - W - 8),
      y: Math.min(rect.bottom + 6, window.innerHeight - 180),
    };
    editing = { recordId, field };
    editDraft = value;
  }
  async function commitEdit() {
    if (!editing || !onCellEdit) return;
    editBusy = true;
    try {
      await onCellEdit(editing.recordId, editing.field, editDraft);
      // оновити локально у відповідній колонці
      for (const c of cols) {
        const idx = c.records.findIndex((r) => r.id === editing!.recordId);
        if (idx >= 0) {
          const v = editDraft;
          const next = { ...c.records[idx]!, values: { ...c.records[idx]!.values } };
          if (v === null || v === '') delete next.values[editing.field.fieldKey];
          else next.values[editing.field.fieldKey] = v;
          c.records[idx] = next;
          c.records = [...c.records];
          break;
        }
      }
      editing = null; editAnchor = null; editDraft = undefined;
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    } finally {
      editBusy = false;
    }
  }
  function cancelEdit() { editing = null; editAnchor = null; editDraft = undefined; }
  function onKeydown(e: KeyboardEvent) { if (e.key === 'Escape' && editing) cancelEdit(); }

  /* ── Card layout ──────────────────────────────────────────────────────────── */
  const fieldByKey = $derived(new Map(fields.map((f) => [f.fieldKey, f])));
  const primaryField = $derived(
    (config.primaryFieldKey ? fieldByKey.get(config.primaryFieldKey) : null) ?? fields[0] ?? null,
  );
  const hasUserConfig = $derived(config.fields.length > 0);
  const bodyFields = $derived.by(() => {
    if (hasUserConfig) {
      return config.fields
        .map((cf) => ({ field: fieldByKey.get(cf.key), showLabel: cf.showLabel }))
        .filter((x): x is { field: ApiField; showLabel: boolean } => !!x.field);
    }
    return fields
      .filter((f) => !f.hidden && f.type !== 'linked_summary' && f.fieldKey !== primaryField?.fieldKey)
      .slice(0, 3)
      .map((f) => ({ field: f, showLabel: true }));
  });
  const sizeClass = $derived.by(() => {
    const s: CardSize = config.cardSize ?? 'medium';
    if (s === 'small') return { card: 'p-2.5', title: 'text-[11.5px]', row: 'text-[10px]' };
    if (s === 'large') return { card: 'p-4', title: 'text-[14px]', row: 'text-[12px]' };
    return { card: 'p-3', title: 'text-[12.5px]', row: 'text-[11px]' };
  });
  function getPrimaryLabel(rec: ApiRecord): string {
    if (!primaryField) return rec.id.slice(0, 8) + '…';
    const v = rec.values[primaryField.fieldKey];
    return v == null || v === '' ? '—' : String(v);
  }
  const anyDroppable = $derived(canDragGroup && !!onCardMove);
</script>

<svelte:window onkeydown={onKeydown} onpointermove={onResizeMove} onpointerup={endResize} />

<!-- Toolbar -->
<div class="mb-2 flex items-center justify-between">
  <button type="button" onclick={onConfigClick}
    class="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold hover:bg-[var(--color-bg-3)]">
    <Icon name="settings" size={14} /> Налаштування картки
  </button>
</div>

{#if loading}
  <div class="flex items-center justify-center py-16 text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else if err}
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
{:else if cols.length === 0}
  <div class="flex items-center justify-center py-16 text-sm text-[var(--color-text-muted-2)] italic">Немає колонок для відображення.</div>
{:else}
  <div class="flex gap-4 overflow-x-auto pb-4 pt-1 min-h-[300px]" onscroll={onBoardScroll}>
    {#each cols as col, i (col.key)}
      {#if collapsed[col.key]}
        <button type="button" onclick={() => toggleCollapse(col.key)}
          class="flex flex-col items-center shrink-0 w-11 rounded-xl border py-3 gap-2 hover:shadow-sm transition-all"
          style="border-color: {col.color.border}; background: {col.color.bg};"
          title="Розгорнути «{displayLabel(col)}»">
          <span class="h-2 w-2 rounded-full" style="background: {col.color.dot};"></span>
          <span class="text-[10px] font-bold" style="color: {col.color.text};">{col.count}</span>
          <span class="[writing-mode:vertical-rl] text-[11px] font-semibold truncate max-h-[180px]" style="color: {col.color.text};">{displayLabel(col)}</span>
        </button>
      {:else}
        <div class="relative flex flex-col shrink-0 rounded-xl border overflow-hidden"
          style="width: {colWidth(col.key)}px; border-color: {col.color.border}; background: {col.color.bg};">
          <!-- Header -->
          <div class="flex items-center justify-between px-3 py-2.5 border-b sticky top-0 z-[1]" style="border-color: {col.color.border}; background: {col.color.bg};">
            <div class="flex items-center gap-2 min-w-0">
              <span class="h-2 w-2 rounded-full shrink-0" style="background: {col.color.dot};"></span>
              <span class="text-[11.5px] font-semibold truncate" style="color: {col.color.text};">{displayLabel(col)}</span>
            </div>
            <div class="flex items-center gap-1 shrink-0 ml-2">
              <span class="text-[10.5px] font-semibold rounded-full px-1.5 py-0.5" style="color: {col.color.text}; background: white;">{col.count}</span>
              {#if selectable}
                {@const allSel = col.records.length > 0 && col.records.every((r) => selectedIds.has(r.id))}
                <button type="button" onclick={() => selectAllInColumn(col)} disabled={selectingCol === col.key}
                  class="grid h-5 w-5 place-items-center rounded text-[10px] disabled:opacity-50 {allSel ? 'bg-[var(--color-pu)] text-white' : 'text-[var(--color-text-muted-2)] hover:bg-white hover:text-[var(--color-pu)]'}"
                  title={allSel ? 'Зняти вибір колонки' : 'Вибрати всі картки колонки'} aria-label="Вибір колонки">{#if selectingCol === col.key}…{:else}<Icon name="checkbox" size={12} />{/if}</button>
              {/if}
              <button type="button" onclick={() => toggleCollapse(col.key)}
                class="text-[var(--color-text-muted-2)] hover:text-[var(--color-pu)] text-xs leading-none"
                title="Згорнути" aria-label="Згорнути колонку"><Icon name="chevronLeft" size={14} /></button>
            </div>
          </div>

          <!-- Cards -->
          <div
            use:dndzone={{
              items: col.records,
              type: 'kanban-cards',
              flipDurationMs: 160,
              dragDisabled: !anyDroppable,
              dropFromOthersDisabled: !anyDroppable,
              dropTargetStyle: { outline: '2px dashed var(--color-pu-2)', outlineOffset: '-3px', borderRadius: '0.5rem' },
            }}
            onconsider={(e) => onConsider(i, e)}
            onfinalize={(e) => onFinalize(i, e)}
            class="flex flex-col gap-2 p-2 overflow-y-auto min-h-[60px] max-h-[calc(100vh-260px)]"
          >
            {#each col.records as rec (rec.id)}
              {@const isSel = selectedIds.has(rec.id)}
              <div animate:flip={{ duration: 160 }}
                class="group/card relative rounded-lg border bg-white {sizeClass.card} transition-all {isSel ? 'border-[var(--color-pu)] ring-1 ring-[var(--color-pu)]' : 'border-[var(--color-border-3)] hover:border-[var(--color-pu-2)] hover:shadow-sm'} {anyDroppable ? 'cursor-grab active:cursor-grabbing' : ''}">
                {#if selectable}
                  <button type="button" onclick={(e) => onCheckboxClick(e, rec.id)}
                    class="absolute top-2 right-2 z-[1] grid h-4 w-4 place-items-center rounded border text-[9px] transition-opacity {isSel ? 'bg-[var(--color-pu)] border-[var(--color-pu)] text-white opacity-100' : 'border-[var(--color-border-3)] bg-white text-transparent opacity-0 group-hover/card:opacity-100'}"
                    title="Вибрати" aria-label="Вибрати картку"><Icon name="check" size={11} /></button>
                {/if}
                <div class="flex items-center gap-2 mb-1.5 pr-5">
                  {#if showAvatar}<Avatar name={getPrimaryLabel(rec)} size={22} />{/if}
                  <button type="button" onclick={() => onRecordClick(rec.id)}
                    class="block flex-1 text-left {sizeClass.title} font-semibold text-[var(--color-text)] truncate leading-snug hover:text-[var(--color-pu)]">
                    {getPrimaryLabel(rec)}
                  </button>
                </div>
                {#each bodyFields as bf (bf.field.fieldKey)}
                  {@const val = rec.values[bf.field.fieldKey]}
                  {@const isEmpty = val == null || val === '' || (Array.isArray(val) && val.length === 0)}
                  {@const editable = isFieldEditable(bf.field)}
                  {#if hasUserConfig || !isEmpty}
                    <div class="group/row flex items-baseline gap-1.5 {sizeClass.row} text-[var(--color-text-muted)] mb-0.5 max-w-full leading-snug">
                      {#if bf.showLabel}<span class="shrink-0 truncate max-w-[90px]">{bf.field.label}:</span>{/if}
                      <span class="font-medium text-[var(--color-text)] min-w-0 truncate whitespace-nowrap flex-1">
                        {#if isEmpty}<span class="text-[var(--color-text-muted-2)]">—</span>{:else}<FieldDisplay field={bf.field} value={val} />{/if}
                      </span>
                      {#if editable}
                        <button type="button" title="Редагувати" onclick={(e) => startEdit(e, rec.id, bf.field, val)}
                          class="shrink-0 grid h-5 w-5 place-items-center rounded-full text-[var(--color-text-muted-2)] opacity-0 group-hover/row:opacity-100 hover:bg-[var(--color-bg-3)] hover:text-[var(--color-pu)] transition-opacity"><Icon name="edit" size={12} /></button>
                      {/if}
                    </div>
                  {/if}
                {/each}
              </div>
            {/each}

            {#if col.records.length === 0}
              <div class="py-6 text-center text-[11px] text-[var(--color-text-muted-2)] italic">Немає записів</div>
            {/if}

            {#if col.records.length < col.count}
              <button type="button" onclick={() => loadMoreCards(col)} disabled={col.loadingMore}
                class="mt-1 rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1.5 text-[10.5px] font-semibold hover:bg-[var(--color-bg-3)] disabled:opacity-60">
                {col.loadingMore ? 'Завантаження…' : `Ще ${col.count - col.records.length}`}
              </button>
            {/if}
          </div>

          <div class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[var(--color-pu-2)]/40"
            role="separator" aria-orientation="vertical" onpointerdown={(e) => startResize(e, col.key)}></div>
        </div>
      {/if}
    {/each}

    {#if facetsHasMore}
      <div class="shrink-0 w-11 flex items-center justify-center">
        <button type="button" onclick={loadMoreColumns} disabled={loadingColumns}
          class="rounded-lg border border-[var(--color-border-3)] bg-white px-2 py-3 text-[10px] font-semibold hover:bg-[var(--color-bg-3)] disabled:opacity-60 [writing-mode:vertical-rl]">
          {loadingColumns ? '…' : 'Ще колонки'}
        </button>
      </div>
    {/if}
  </div>
{/if}

<!-- Edit popover -->
{#if editing && editAnchor}
  <button type="button" class="fixed inset-0 z-40 cursor-default" aria-label="Закрити" onclick={cancelEdit}></button>
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div class="fixed z-50 w-[300px] rounded-xl border border-[var(--color-border-3)] bg-white shadow-2xl p-3.5"
    style="left: {editAnchor.x}px; top: {editAnchor.y}px;" role="dialog">
    <div class="flex items-center gap-2 mb-2">
      <span class="text-[var(--color-text-muted-2)] inline-flex"><Icon name="edit" size={14} /></span>
      <span class="text-[12px] font-semibold">{editing.field.label}</span>
    </div>
    <FieldEditor field={editing.field} value={editDraft} onChange={(v) => (editDraft = v)} />
    <div class="mt-3 flex justify-end gap-2">
      <button type="button" onclick={cancelEdit} disabled={editBusy}
        class="rounded-lg border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide hover:bg-[var(--color-bg-3)]">Скасувати</button>
      <button type="button" onclick={commitEdit} disabled={editBusy}
        class="rounded-lg bg-[var(--color-pu)] text-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide hover:opacity-90 disabled:opacity-50">{editBusy ? '…' : 'Зберегти'}</button>
    </div>
  </div>
{/if}
